import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useWordsByUnitQuery } from '@entities/Word';
import { fetchCramCardStats, fetchCramToday, type BuilderSettings, type ExerciseKind } from '@features/Session';
import { cn } from '@shared/lib';
import {
  EXERCISE_ORDER,
  MAX_SESSION_CARDS,
  TRAINING_PRESETS,
  matchingPreset,
  reconcilePool,
  resolveBuilderStart,
  type BuilderDefaults,
} from '../model/builderConfig';
import { eligibleCardIds, selectFilteredIds, type CardFilter } from '../model/cardFilters';
import { cardShows, totalWeight } from '../model/cardWeight';
import { BuilderIcon } from './BuilderIcons';
import { BuilderPreview } from './BuilderPreview';
import { CardPicker, type ReadyWord } from './CardPicker';
import './BuilderPanel.css';

interface BuilderPanelProps {
  moduleId: string;
  moduleTitle: string;
  backLabel?: string;
  starting: boolean;
  replacingActive?: boolean;
  savedSettings: BuilderSettings | null;
  /** Состав из адреса (`?preset=&n=&boss=`): упражнения — только без сохранённого, число и матч — всегда. */
  defaults?: BuilderDefaults;
  onBack: () => void;
  onStart: (exercises: ExerciseKind[], stateIds: string[] | undefined, withBoss: boolean) => void;
}

/** Размеры набора из макета. 30 убрано: точный состав собирается «вручную». */
const SIZES = [10, 20] as const;

/** Сохранённый состав собран вручную, если его размер не совпадает ни с одной кнопкой
 *  количества: наборы 10/20 и «Все» по сохранённым ID неотличимы от ручного выбора. */
function isManualSet(size: number, total: number, savedSettings: BuilderSettings | null): boolean {
  if (!savedSettings?.stateIds) return false;
  const max = Math.min(total, MAX_SESSION_CARDS);
  return size < max && !SIZES.some((option) => option === size);
}

/** Пресет задаёт форматы, набор карточек — независимая ось. Конкретный состав
 *  бэкенд сохраняет при запуске (см. startCramSession), а не при редактировании. */
export function BuilderPanel({
  moduleId,
  moduleTitle,
  backLabel = moduleTitle,
  starting,
  replacingActive = false,
  savedSettings,
  defaults,
  onBack,
  onStart,
}: BuilderPanelProps) {
  const { t } = useTranslation();
  // Упражнения из адреса не перебивают сохранённый состав — это выбор ученика. Число карточек
  // (ссылка «Повторить 10 слов») и финальный матч из адреса действуют всегда (B1, 2026-10-20).
  const [initial] = useState(() => resolveBuilderStart(savedSettings, defaults));
  const poolSettings = initial.poolSettings;
  const wordsQuery = useWordsByUnitQuery(moduleId);
  const statsQuery = useQuery({
    queryKey: ['cram-card-stats', moduleId],
    queryFn: () => fetchCramCardStats(moduleId),
    staleTime: 0,
    retry: 1,
  });
  // «На сегодня» (решение владельца 2026-10-21): сначала повторить, потом новые — порядок задаёт сервер.
  const todayQuery = useQuery({
    queryKey: ['cram-today', moduleId],
    queryFn: () => fetchCramToday(moduleId),
    staleTime: 0,
    retry: 1,
  });
  const todayIds = useMemo(() => todayQuery.data?.stateIds ?? [], [todayQuery.data]);
  const cards = useMemo(
    () =>
      (wordsQuery.data ?? []).filter(
        (word): word is ReadyWord =>
          !!word.stateId && !!word.ru && !word.isNeedAction && !word.isHidden,
      ),
    [wordsQuery.data],
  );
  const allIds = useMemo(() => cards.map((card) => card.stateId), [cards]);
  // Строка — слово в части речи, а карточек у неё по числу отмеченных смыслов: всё считаем
  // в карточках, как они созданы (2026-10-06).
  const shows = useMemo(() => new Map(cards.map((card) => [card.stateId, cardShows(card)])), [cards]);
  const weight = useCallback((id: string) => shows.get(id) ?? 1, [shows]);
  const [selected, setSelected] = useState(() => initial.exercises);
  const [pool, setPool] = useState<Set<string> | null>(null);
  const [cardFilter, setCardFilter] = useState<CardFilter>('all');
  const [requestedCount, setRequestedCount] = useState<number | 'all'>(initial.requestedCount);
  const [withBoss, setWithBoss] = useState(initial.withBoss);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Состав собран в «Выбрать вручную»: фильтры и количество не подсвечиваются,
  // вместо них — плашка «Выбрано вручную n». Фильтр или количество возвращают набор.
  const [manual, setManual] = useState(false);
  // Формат под курсором — он же показан в превью. По умолчанию первый выбранный.
  const [hovered, setHovered] = useState<ExerciseKind>('input');

  useEffect(() => {
    if (pool !== null || !wordsQuery.isSuccess) return;
    // Из списка модулей — «На сегодня»: ждём набор с сервера; пусто или ошибка — обычный набор.
    if (initial.cardFilter === 'today') {
      if (todayQuery.isPending) return;
      const ids = eligibleCardIds(allIds, [], 'today', todayIds);
      if (ids.length > 0) {
        setCardFilter('today');
        setPool(new Set(selectFilteredIds(ids, initial.defaultCount, 'today', weight)));
        return;
      }
    }
    const restored = reconcilePool(allIds, poolSettings, weight, initial.defaultCount);
    setPool(restored);
    const restoredCards = totalWeight(restored, weight);
    if (isManualSet(restoredCards, totalWeight(allIds, weight), poolSettings)) {
      setManual(true);
      setRequestedCount(20);
    } else if (poolSettings?.stateIds) {
      setRequestedCount(restoredCards);
    }
  }, [
    allIds,
    pool,
    poolSettings,
    wordsQuery.isSuccess,
    weight,
    initial.defaultCount,
    initial.cardFilter,
    todayQuery.isPending,
    todayIds,
  ]);

  const eligibleIds = useMemo(
    () => eligibleCardIds(allIds, statsQuery.data ?? [], cardFilter, todayIds),
    [allIds, statsQuery.data, cardFilter, todayIds],
  );
  const eligibleSet = useMemo(() => new Set(eligibleIds), [eligibleIds]);
  const filteredCards = useMemo(() => {
    const byId = new Map(cards.map((card) => [card.stateId, card]));
    return eligibleIds.map((id) => byId.get(id)!).filter(Boolean);
  }, [cards, eligibleIds]);
  const mistakeCounts = useMemo(
    () => new Map((statsQuery.data ?? []).map((item) => [item.stateId, item.mistakeCount])),
    [statsQuery.data],
  );
  const mistakeCardCount = useMemo(
    () => totalWeight(eligibleCardIds(allIds, statsQuery.data ?? [], 'mistakes'), weight),
    [allIds, statsQuery.data, weight],
  );
  const frequentCardCount = useMemo(
    () => totalWeight(eligibleCardIds(allIds, statsQuery.data ?? [], 'frequent'), weight),
    [allIds, statsQuery.data, weight],
  );
  const todayCardCount = useMemo(
    () => totalWeight(eligibleCardIds(allIds, [], 'today', todayIds), weight),
    [allIds, todayIds, weight],
  );
  const poolSet = useMemo(
    () => new Set(allIds.filter((id) => pool?.has(id) && eligibleSet.has(id))),
    [allIds, pool, eligibleSet],
  );
  const selectedCount = totalWeight(poolSet, weight);
  const totalCards = totalWeight(allIds, weight);
  const eligibleCards = totalWeight(eligibleIds, weight);
  const allSelected = cards.length > 0 && cards.length === poolSet.size;
  const picked = EXERCISE_ORDER.filter((kind) => selected.has(kind));
  const preset = matchingPreset(selected);
  const canRestore = wordsQuery.isSuccess && totalCards > 0 && !starting;
  const filterReady = (filter: CardFilter) =>
    filter === 'all' || (filter === 'today' ? todayQuery.isSuccess : statsQuery.isSuccess);
  const canChoose = canRestore && eligibleIds.length > 0 && filterReady(cardFilter);
  const canStart =
    canChoose && selectedCount > 0 && selectedCount <= MAX_SESSION_CARDS && picked.length > 0;
  const maxCards = Math.min(eligibleCards, MAX_SESSION_CARDS);
  const sizes = SIZES.filter((size) => size < maxCards);

  const chooseFilter = (filter: CardFilter) => {
    if (!canRestore || !filterReady(filter)) return;
    const ids = eligibleCardIds(allIds, statsQuery.data ?? [], filter, todayIds);
    setManual(false);
    setCardFilter(filter);
    setPool(
      new Set(
        selectFilteredIds(ids, requestedCount === 'all' ? MAX_SESSION_CARDS : requestedCount, filter, weight),
      ),
    );
  };
  const chooseSize = (size: number | 'all') => {
    if (!canChoose) return;
    setManual(false);
    setRequestedCount(size);
    setPool(
      new Set(selectFilteredIds(eligibleIds, size === 'all' ? MAX_SESSION_CARDS : size, cardFilter, weight)),
    );
  };
  const toggleExercise = (kind: ExerciseKind) => {
    if (starting) return;
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  };
  const choosePreset = (exercises: readonly ExerciseKind[]) => {
    if (starting) return;
    setSelected(new Set(exercises));
  };
  const start = () => {
    if (!canStart) return;
    onStart(picked, cardFilter === 'all' && allSelected ? undefined : [...poolSet], withBoss);
  };

  const filterCounts: Record<CardFilter, number | null> = {
    today: todayQuery.isSuccess ? todayCardCount : null,
    all: totalCards,
    mistakes: statsQuery.isSuccess ? mistakeCardCount : null,
    frequent: statsQuery.isSuccess ? frequentCardCount : null,
  };

  return (
    <div className="bx">
      <button type="button" className="bx-back" onClick={onBack} disabled={starting}>
        ← {backLabel}
      </button>

      <header className="bx-head">
        <div className="bx-head-copy">
          <p className="bx-eyebrow">
            <span>{t('cram.kicker')}</span>
            <i aria-hidden>/</i>
            <span>{moduleTitle}</span>
          </p>
          <h1>{t('cram.builder.setup.format')}</h1>
        </div>
        <div className="bx-presets" role="group" aria-label={t('cram.builder.setup.format')}>
          {TRAINING_PRESETS.map((item) => (
            <button
              type="button"
              key={item.id}
              className={cn('bx-preset', preset === item.id && 'is-on')}
              aria-pressed={preset === item.id}
              disabled={starting}
              onClick={() => choosePreset(item.exercises)}
            >
              {t(`cram.builder.setup.presets.${item.id}.short`)}
            </button>
          ))}
          {preset === 'custom' && (
            <span className="bx-preset is-custom is-on">
              {t('cram.builder.setup.presets.custom.short')}
            </span>
          )}
        </div>
      </header>

      <div className="bx-grid">
        <div className="bx-col">
          {/* ── 01 форматы ─────────────────────────────────────────── */}
          <section className="bx-block" aria-labelledby="bx-formats">
            <div className="bx-blockhead">
              <span className="bx-num" aria-hidden>
                01
              </span>
              <h2 id="bx-formats">{t('cram.builder.setup.formats')}</h2>
              <span className="bx-meta">
                {picked.length
                  ? t('cram.builder.setup.pickedOf', { count: picked.length })
                  : t('cram.builder.setup.nonePicked')}
              </span>
            </div>
            <div className="bx-fmts">
              {EXERCISE_ORDER.map((kind) => (
                <button
                  key={kind}
                  type="button"
                  className={cn('bx-fmt', selected.has(kind) && 'is-on', hovered === kind && 'is-hot')}
                  data-k={kind}
                  aria-pressed={selected.has(kind)}
                  disabled={starting}
                  onMouseEnter={() => setHovered(kind)}
                  onFocus={() => setHovered(kind)}
                  onClick={() => toggleExercise(kind)}
                >
                  <span className="bx-fmt-ico">
                    <BuilderIcon name={kind} />
                  </span>
                  <span className="bx-fmt-copy">
                    <strong>{t(`cram.builder.ex.${kind}.title`)}</strong>
                    <span>{t(`cram.builder.ex.${kind}.desc`)}</span>
                  </span>
                  <span className="bx-switch" aria-hidden />
                </button>
              ))}
            </div>
            <p className="bx-note">{t('cram.builder.setup.manualNote')}</p>
          </section>

          {/* ── 02 карточки ────────────────────────────────────────── */}
          <section className="bx-block" aria-labelledby="bx-cards" aria-busy={wordsQuery.isPending}>
            <div className="bx-blockhead">
              <span className="bx-num" aria-hidden>
                02
              </span>
              <h2 id="bx-cards">{t('cram.builder.setup.cardsShort')}</h2>
              <span className="bx-meta">
                {wordsQuery.isSuccess && t('cram.builder.setup.available', { count: totalCards })}
              </span>
            </div>

            {wordsQuery.isPending ? (
              <p className="bx-note" role="status">
                {t('cram.builder.loading')}
              </p>
            ) : wordsQuery.isError ? (
              <div className="bx-alert" role="alert">
                <p>{t('cram.loadError')}</p>
                <button type="button" className="bx-text" onClick={() => void wordsQuery.refetch()}>
                  {t('cram.retry')}
                </button>
              </div>
            ) : totalCards === 0 ? (
              <p className="bx-note" role="status">
                {t('cram.noCards')}
              </p>
            ) : (
              <>
                <div className="bx-chips" role="group" aria-label={t('cram.builder.filters.label')}>
                  {(['today', 'all', 'mistakes', 'frequent'] as const).map((filter) => (
                    <button
                      key={filter}
                      type="button"
                      className={cn('bx-chip', !manual && cardFilter === filter && 'is-on')}
                      aria-pressed={!manual && cardFilter === filter}
                      disabled={
                        !canRestore || !filterReady(filter) || (filter === 'today' && todayCardCount === 0)
                      }
                      onClick={() => chooseFilter(filter)}
                    >
                      {t(`cram.builder.filters.${filter}`)}
                      <span>{filterCounts[filter] ?? '…'}</span>
                    </button>
                  ))}
                  {manual && (
                    <button
                      type="button"
                      className="bx-chip is-on"
                      aria-pressed
                      disabled={!canChoose}
                      onClick={() => setPickerOpen(true)}
                    >
                      {t('cram.builder.filters.manual')}
                      <span>{selectedCount}</span>
                    </button>
                  )}
                </div>

                {statsQuery.isError && (
                  <p className="bx-note" role="status">
                    {t('cram.builder.filters.loadError')}{' '}
                    <button type="button" className="bx-text" onClick={() => void statsQuery.refetch()}>
                      {t('cram.retry')}
                    </button>
                  </p>
                )}

                {eligibleIds.length === 0 ? (
                  <div className="bx-alert" role="status">
                    <strong>{t('cram.builder.filters.emptyTitle')}</strong>
                    <p>{t('cram.builder.filters.emptyBody')}</p>
                    <button type="button" className="bx-text" onClick={() => chooseFilter('all')}>
                      {t('cram.builder.filters.showAll')}
                    </button>
                  </div>
                ) : (
                  <div className="bx-sizes" role="group" aria-label={t('cram.builder.setup.amount')}>
                    {sizes.map((size) => (
                      <button
                        type="button"
                        key={size}
                        className={cn('bx-size', !manual && selectedCount === size && 'is-on')}
                        aria-pressed={!manual && selectedCount === size}
                        disabled={!canChoose}
                        onClick={() => chooseSize(size)}
                      >
                        {size}
                      </button>
                    ))}
                    <button
                      type="button"
                      className={cn('bx-size', !manual && selectedCount === maxCards && 'is-on')}
                      aria-pressed={!manual && selectedCount === maxCards}
                      disabled={!canChoose}
                      onClick={() => chooseSize('all')}
                    >
                      {eligibleCards <= MAX_SESSION_CARDS
                        ? t('cram.builder.setup.all')
                        : MAX_SESSION_CARDS}
                    </button>
                    <button
                      type="button"
                      className="bx-size bx-size-manual"
                      disabled={!canChoose}
                      onClick={() => setPickerOpen(true)}
                    >
                      {t('cram.builder.setup.chooseCards')}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>

          {/* ── 03 финальный матч ──────────────────────────────────── */}
          <section className="bx-block" aria-labelledby="bx-final">
            <div className="bx-blockhead">
              <span className="bx-num" aria-hidden>
                03
              </span>
              <h2 id="bx-final">{t('cram.builder.setup.finalMatch')}</h2>
            </div>
            <button
              type="button"
              className={cn('bx-fmt', 'bx-final', withBoss && 'is-on')}
              aria-pressed={withBoss}
              disabled={starting}
              onClick={() => setWithBoss((v) => !v)}
            >
              <span className="bx-fmt-ico">
                <BuilderIcon name="spark" />
              </span>
              <span className="bx-fmt-copy">
                <strong>{t('cram.builder.setup.finalMatch')}</strong>
                <span>{t('cram.builder.setup.finalMatchDesc')}</span>
              </span>
              <span className="bx-switch" aria-hidden />
            </button>
          </section>
        </div>

        {/* ── превью ───────────────────────────────────────────────── */}
        <div className="bx-col bx-preview">
          <div className="bx-prevhead">
            <span>{t('cram.builder.setup.previewLabel')}</span>
            <span className="bx-prevname" data-k={hovered}>
              {t(`cram.builder.ex.${hovered}.title`)}
            </span>
          </div>
          <BuilderPreview kind={hovered} />
          <p className="bx-note">{t(`cram.builder.ex.${hovered}.rule`)}</p>
        </div>
      </div>

      <div className="bx-foot">
        <div className="bx-sum">
          <strong>{t('training.cardCount', { count: selectedCount })}</strong>
          <i aria-hidden>·</i>
          <span className="bx-sum-fmts">
            {picked.map((kind) => (
              <span key={kind} className="bx-sum-ico" data-k={kind}>
                <BuilderIcon name={kind} />
              </span>
            ))}
            <span>
              {picked.length
                ? picked.map((kind) => t(`cram.builder.ex.${kind}.title`).toLowerCase()).join(', ')
                : t('cram.builder.setup.nonePicked')}
            </span>
          </span>
          <i aria-hidden>·</i>
          <span>
            {withBoss
              ? t('cram.builder.setup.finalMatch').toLowerCase()
              : t('cram.builder.setup.finalMatchOff')}
          </span>
          {replacingActive && <span className="bx-sum-warn">{t('cram.replaceNote')}</span>}
        </div>
        <button
          type="button"
          className="bx-start"
          disabled={!canStart}
          aria-busy={starting}
          onClick={start}
        >
          {starting
            ? t('cram.builder.starting')
            : picked.length === 0
              ? t('cram.builder.startHint')
              : t('cram.builder.start')}
          <BuilderIcon name="arrow" />
        </button>
      </div>

      {pickerOpen && (
        <CardPicker
          cards={filteredCards}
          mistakeCounts={mistakeCounts}
          pool={poolSet}
          onApply={(next) => {
            const changed = next.size !== poolSet.size || [...next].some((id) => !poolSet.has(id));
            if (changed) {
              // Ручной состав живёт поверх всех карточек модуля: повторное открытие
              // пикера показывает весь список, а не только карточки прежнего фильтра.
              setPool(next);
              setCardFilter('all');
              setManual(next.size < cards.length);
            }
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}
